/**
 * Mon compte v2, membre connecté (SPEC § 4.4 : MC1 à MC3, MC5 à MC7, MC9, MC10 ; retours M2 à M6).
 * Maquette : 17-compte.
 *
 * Colonne principale, DANS CET ORDRE (M3) : 1) Mes cours à venir 2) Mes crédits actifs 3) Mon
 * abonnement (masqué sans abonnement actif, sans bouton pause / résiliation) 4) Mes chiffres
 * 5) Mon historique (3 derniers cours). Colonne latérale : Parrainage, Moyen de paiement, Mes
 * informations (le mot de passe se change dans « Modifier mes informations » depuis les retours du 12/10). Pas de
 * menu interne (EN3), pas de carte « Confidentialité » (MC8, lot D non retenu).
 *
 * Effets repris de la v1 (screens/Account/index.tsx, UserProfile.tsx) : réclamation d'un parrainage
 * en attente après connexion, défilement vers l'ancre de l'URL (#parrainage, #mon-abonnement).
 */
import { useToast } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { clearPendingReferral, readPendingReferral, useReferralContext } from 'hooks/useReferralContext';
import { useUserContext } from 'providers/useUserContext';
import { useEffect, useRef, useState } from 'react';
import { HTTP } from 'utils/http';
import { History } from './components/History';
import { IdentityCard } from './components/Identity';
import { InformationsCard } from './components/Informations';
import { PaymentMethodCard } from './components/PaymentMethod';
import { ReferralCard } from './components/Referral';
import { Stats } from './components/Stats';
import { Subscription } from './components/Subscription';
import { AccountShell } from './components/Ui';
import { Upcoming } from './components/Upcoming';
import { Wallets } from './components/Wallets';
import { useScrollToHash } from './hooks';
import { ReferralNotices } from './ReferralNotices';

export function AccountHome() {
  const { isLoggedIn, logout } = useUserContext();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  // = index.tsx : bandeaux de parrainage aussi pour un membre connecté.
  const referralContext = useReferralContext();
  useReferralClaim(isLoggedIn);
  useScrollToHash();

  // = index.tsx handleLogout()
  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
    } catch (error) {
      // Erreur déjà traitée par le contexte.
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <AccountShell className="mmv2-acct--home">
      <div className="mmv2-acct-grid">
        <div className="mmv2-acct-main">
          <div className="mmv2-acct-titlebar">
            <h1 className="mmv2-acct-title">Mon compte</h1>
            <button
              type="button"
              className="mmv2-btn mmv2-btn--sm mmv2-acct-logout"
              onClick={handleLogout}
              disabled={isLoggingOut}
              aria-busy={isLoggingOut}
            >
              {isLoggingOut ? 'Déconnexion…' : 'Se déconnecter'}
            </button>
          </div>
          <ReferralNotices context={referralContext} />
          <IdentityCard />
          <Upcoming />
          <Wallets />
          <Subscription />
          <Stats />
          <History />
        </div>
        <aside className="mmv2-acct-aside" aria-label="Parrainage, paiement et informations">
          <ReferralCard />
          <PaymentMethodCard />
          <InformationsCard />
        </aside>
      </div>
    </AccountShell>
  );
}

/**
 * = screens/Account/index.tsx : parrainage d'un client qui avait déjà un compte. Une fois connecté,
 * on demande au serveur de lui appliquer le parrainage en attente ; une seule tentative par montage,
 * contexte vidé dès que le serveur a tranché, gardé sur erreur réseau.
 */
function useReferralClaim(isLoggedIn: boolean | undefined) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const claimStartedRef = useRef(false);

  useEffect(() => {
    if (!isLoggedIn || claimStartedRef.current) return;
    const pending = readPendingReferral();
    if (!pending) return;
    claimStartedRef.current = true;
    HTTP.post<{
      status: 'claimed' | 'not_eligible' | 'retry';
      reason: 'claimed' | 'history' | 'other';
      refereeCredited: boolean;
    }>('/referral/claim', {
      referrerClientId: pending.referrerClientId,
      campaignId: pending.campaignId,
    })
      .setHeader('Content-Type', 'application/json')
      .send()
      .then((res) => {
        if (res?.status === 'retry') return;
        clearPendingReferral();
        if (res?.status === 'claimed') {
          queryClient.invalidateQueries({ queryKey: ['/client/services'] });
          queryClient.invalidateQueries({ queryKey: ['/client/purchases'] });
          toast({
            status: 'success',
            title: 'Parrainage appliqué',
            description: res.refereeCredited
              ? 'Votre cadeau de parrainage a été ajouté à votre compte.'
              : 'Votre parrainage est bien enregistré.',
            duration: 8000,
            isClosable: true,
          });
        } else if (res?.reason === 'history') {
          toast({
            status: 'info',
            title: 'L’offre de parrainage ne s’applique pas à votre compte',
            description: 'Elle est réservée aux personnes qui découvrent le studio.',
            duration: 8000,
            isClosable: true,
          });
        }
      })
      .catch(() => {
        // Contexte conservé : nouvelle tentative au prochain passage.
      });
  }, [isLoggedIn, queryClient, toast]);
}
